import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Trash2,
  Calendar,
  Users,
  Clock,
  Send,
  Loader,
  LayoutGrid,
  List,
  Table2,
} from "lucide-react";
import { adminTimesheetApi } from "../../firebase/apis/adminTimesheetApi";
import { payrollApi } from "../../firebase/apis/payrollApi";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { FormControl, Autocomplete, TextField } from "@mui/material";
dayjs.extend(utc);

// ─── View Mode Config ───────────────────────────────────────────────────────
const VIEW_MODES = [
  { key: "table", label: "Table", Icon: Table2,    title: "Dense table — see all coaches at once" },
  { key: "cards", label: "Cards", Icon: LayoutGrid, title: "Card grid view" },
  { key: "list",  label: "List",  Icon: List,       title: "Detailed list view" },
];

// Status badge helper
const StatusBadge = ({ status }) => {
  const cfg = {
    submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
    approved:  "bg-green-100 text-green-800 border-green-200",
    rejected:  "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${cfg[status] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const AdminTimesheetDashboard = () => {
  const [timesheets, setTimesheets] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [timesheetSearchTerm, setTimesheetSearchTerm] = useState("");
  const [activeView, setActiveView] = useState("coaches");
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [coachesPage, setCoachesPage] = useState(1);
  const [timesheetsPage, setTimesheetsPage] = useState(1);
  const [coachesPerPage, setCoachesPerPage] = useState(200);
  const [viewMode, setViewMode] = useState("table");
  const [timesheetsPerPage, setTimesheetsPerPage] = useState(10);
  const [selectedCoaches, setSelectedCoaches] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [payPeriodOpen, setPayPeriodOpen] = useState(false);
  const payPeriodRef = useRef(null);

  // Toast notifications
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "success" }), 4000);
  };

  // Confirmation modal
  const [confirmModal, setConfirmModal] = useState({ show: false, title: "", message: "", onConfirm: null });
  const showConfirm = (title, message, onConfirm) => setConfirmModal({ show: true, title, message, onConfirm });

  // Review modal state
  const [reviewCoach, setReviewCoach] = useState(null);
  const [reviewTimesheets, setReviewTimesheets] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewPayrollProfile, setReviewPayrollProfile] = useState(null);
  const [reviewAdminNotes, setReviewAdminNotes] = useState("");
  const [reviewActionLoading, setReviewActionLoading] = useState(false);
  const [reviewRejectOpen, setReviewRejectOpen] = useState(false);
  const [reviewRejectReason, setReviewRejectReason] = useState("");
  const [reviewActionType, setReviewActionType] = useState("reject");
  const [reviewSelectedIds, setReviewSelectedIds] = useState([]);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCreatePeriodModal, setShowCreatePeriodModal] = useState(false);
  const [periodForm, setPeriodForm] = useState({ startDate: '', endDate: '', label: '' });
  const [creatingPeriod, setCreatingPeriod] = useState(false);

  // ─── Data Loading ───────────────────────────────────────────────────────────
  const loadPayrollProfiles = async () => {
    try {
      const data = await payrollApi.getProfiles();
      setProfiles(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const timesheetsData = await adminTimesheetApi.getAllTimesheets();
      const timesheetsArray = timesheetsData.data || [];
      setTimesheets(timesheetsArray);
      calculateStats(timesheetsArray);
      extractCoaches(timesheetsArray);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      showToast("Failed to load dashboard data", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    await Promise.all([fetchDashboardData(), loadPayrollProfiles(), loadPayPeriods()]);
  };

  const loadPayPeriods = async () => {
    try {
      const data = await payrollApi.getPayPeriods();
      const periods = Array.isArray(data) ? data : [];
      periods.sort((a, b) => {
        const aTime = a.createdAt?._seconds || a.createdAt || 0;
        const bTime = b.createdAt?._seconds || b.createdAt || 0;
        return bTime - aTime;
      });
      setPayPeriods(periods);
    } catch { /* ignore */ }
  };

  const silentRefresh = async () => {
    try {
      const timesheetsData = await adminTimesheetApi.getAllTimesheets();
      const timesheetsArray = timesheetsData.data || [];
      setTimesheets(timesheetsArray);
      calculateStats(timesheetsArray);
      extractCoaches(timesheetsArray);
    } catch { /* silent */ }
    try {
      const data = await payrollApi.getProfiles();
      setProfiles(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    try {
      const data = await payrollApi.getPayPeriods();
      const periods = Array.isArray(data) ? data : [];
      periods.sort((a, b) => {
        const aTime = a.createdAt?._seconds || a.createdAt || 0;
        const bTime = b.createdAt?._seconds || b.createdAt || 0;
        return bTime - aTime;
      });
      setPayPeriods(periods);
    } catch { /* silent */ }
  };

  useEffect(() => { loadAllData(); }, []);

  useEffect(() => {
    const handleClick = (e) => { if (payPeriodRef.current && !payPeriodRef.current.contains(e.target)) setPayPeriodOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredCoaches(coaches.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchTerm.toLowerCase())
      ));
    } else {
      setFilteredCoaches(coaches);
    }
  }, [searchTerm, coaches]);

  useEffect(() => { setCoachesPage(1); }, [searchTerm, coaches]);
  useEffect(() => { setTimesheetsPage(1); }, [timesheets]);
  useEffect(() => { setTimesheetsPage(1); }, [timesheetSearchTerm]);
  useEffect(() => { setCoachesPage(1); }, [coachesPerPage]);
  useEffect(() => { setTimesheetsPage(1); }, [timesheetsPerPage]);

  const calculateStats = (timesheetsData) => {
    setStats({
      total: timesheetsData.length,
      pending: timesheetsData.filter((ts) => ts.status === "submitted").length,
      approved: timesheetsData.filter((ts) => ts.status === "approved").length,
      rejected: timesheetsData.filter((ts) => ts.status === "rejected").length,
    });
  };

  const extractCoaches = (timesheetsData) => {
    const coachMap = new Map();
    timesheetsData.forEach((timesheet) => {
      if (timesheet.coachId) {
        const coachName = timesheet.name?.trim() || "Unknown Coach";
        const coachEmail = timesheet.email?.trim() || "No email available";
        if (!coachMap.has(timesheet.coachId)) {
          coachMap.set(timesheet.coachId, { id: timesheet.coachId, name: coachName, email: coachEmail, timesheetCount: 1 });
        } else {
          coachMap.get(timesheet.coachId).timesheetCount += 1;
        }
      }
    });
    const coachesArray = Array.from(coachMap.values());
    setCoaches(coachesArray);
    setFilteredCoaches(coachesArray);
  };

  // ─── Review Modal ───────────────────────────────────────────────────────────
  const getPayPeriodRange = (entries) => {
    if (!entries?.length) return "N/A";
    const dates = entries.filter((ts) => ts.date).map((ts) => dayjs.utc(ts.date)).filter((d) => d.isValid());
    if (!dates.length) return "N/A";
    let min = dates[0], max = dates[0];
    dates.forEach((d) => { if (d.isBefore(min)) min = d; if (d.isAfter(max)) max = d; });
    return `${min.format("MMM D, YYYY")} – ${max.format("MMM D, YYYY")}`;
  };

  const handleCoachClick = async (coachId) => {
    const coach = coaches.find((c) => c.id === coachId);
    if (!coach) return;
    setReviewCoach(coach);
    setReviewLoading(true);
    setReviewTimesheets([]);
    setReviewPayrollProfile(null);
    setReviewAdminNotes("");
    setReviewSelectedIds([]);
    setSelectedPeriodId('');
    setCustomStartDate('');
    setCustomEndDate('');
    try {
      const profile = profiles.find((p) => p.coachId === coachId);
      setReviewPayrollProfile(profile || null);
      const res = await adminTimesheetApi.getCoachTimesheets(coachId, { page: 1, limit: 100 });
      const rd = res?.data || {};
      const timesheets = rd.timesheets || [];
      setReviewTimesheets(timesheets);
      setReviewSelectedIds([]);
    } catch (err) {
      console.error("Failed to load coach timesheets:", err);
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReviewApproveAll = async () => {
    if (reviewSelectedIds.length === 0) { showToast("No timesheets selected to approve.", "warning"); return; }
    let periodId = selectedPeriodId;
    if (periodId === '__custom__') {
      if (!customStartDate || !customEndDate) {
        showToast("Please select both start and end dates.", "warning");
        return;
      }
      const existing = payPeriods.find(p => p.startDate === customStartDate && p.endDate === customEndDate);
      if (!existing) {
        showToast("No pay period exists for this date range. Create one to proceed.", "warning");
        setPeriodForm({ startDate: customStartDate, endDate: customEndDate, label: '' });
        setShowCreatePeriodModal(true);
        return;
      }
      periodId = existing.id;
    }
    if (!periodId) { showToast("Please select a Pay Period before approving.", "warning"); return; }
    setReviewActionLoading(true);
    try {
      const result = await adminTimesheetApi.bulkApproveTimesheets(reviewSelectedIds, reviewAdminNotes, true, periodId);
      if (result.success) {
        showToast(result.message || `Approved ${reviewSelectedIds.length} timesheet(s).`, "success");
        closeReview();
        silentRefresh();
      } else {
        showToast(`Error: ${result.message}`, "error");
      }
    } catch (err) {
      showToast("Failed to approve: " + err.message, "error");
    } finally {
      setReviewActionLoading(false);
    }
  };

  const getPeriodDisplayLabel = (p) => {
    if (!p) return "";
    if (p.label) return p.label;
    if (p.startDate) {
      const s = dayjs(p.startDate).format('MM-DD-YYYY');
      const e = p.endDate ? dayjs(p.endDate).format('MM-DD-YYYY') : '';
      return `${s} – ${e}`;
    }
    return p.id;
  };

  const handleReviewRejectAll = async () => {
    if (reviewSelectedIds.length === 0) { showToast("No timesheets selected to reject.", "warning"); return; }
    if (!reviewRejectReason.trim()) { showToast("Please provide a rejection reason.", "warning"); return; }
    setReviewActionLoading(true);
    try {
      const result = await adminTimesheetApi.bulkRejectTimesheets(reviewSelectedIds, reviewRejectReason);
      if (result.success) {
        showToast(result.message || `Rejected ${reviewSelectedIds.length} timesheet(s).`, "success");
        setReviewRejectReason("");
        setReviewRejectOpen(false);
        closeReview();
        silentRefresh();
      } else {
        showToast(`Error: ${result.message}`, "error");
      }
    } catch (err) {
      showToast("Failed to reject: " + err.message, "error");
    } finally {
      setReviewActionLoading(false);
    }
  };

  const handleRejectSingle = async (tsId) => {
    if (!reviewRejectReason.trim()) { showToast("Please provide a rejection reason.", "warning"); return; }
    setReviewActionLoading(true);
    try {
      const result = await adminTimesheetApi.bulkRejectTimesheets([tsId], reviewRejectReason);
      if (result.success) {
        showToast("Timesheet rejected.", "success");
        setReviewRejectOpen(false);
        setReviewRejectReason("");
        silentRefresh();
        setReviewTimesheets((prev) => prev.map((ts) => ts.id === tsId ? { ...ts, status: 'rejected' } : ts));
        setReviewSelectedIds((prev) => prev.filter((id) => id !== tsId));
      } else {
        showToast(`Error: ${result.message}`, "error");
      }
    } catch (err) {
      showToast("Failed to reject: " + err.message, "error");
    } finally {
      setReviewActionLoading(false);
    }
  };

  const handleConfirmRequestCorrection = async () => {
    const reason = reviewRejectReason.trim();
    if (!reason) { showToast("Please enter a reason.", "warning"); return; }
    if (reviewSelectedIds.length === 0) return;
    setReviewActionLoading(true);
    try {
      await adminTimesheetApi.bulkRejectTimesheets(reviewSelectedIds, reason);
      showToast("Correction requested. Coaches will see the reason and can resubmit.", "success");
      setReviewRejectOpen(false);
      setReviewRejectReason("");
      closeReview();
      silentRefresh();
    } catch (err) {
      showToast("Failed: " + err.message, "error");
    } finally {
      setReviewActionLoading(false);
    }
  };

  const closeReview = () => {
    setReviewCoach(null);
    setReviewTimesheets([]);
    setReviewPayrollProfile(null);
    setReviewAdminNotes("");
    setReviewRejectOpen(false);
    setReviewRejectReason("");
    setReviewActionType("reject");
    setReviewSelectedIds([]);
    setSelectedPeriodId('');
  };

  // ─── Bulk Actions ───────────────────────────────────────────────────────────
  const handleViewToggle = () => {
    setActiveView(activeView === "coaches" ? "timesheets" : "coaches");
    setSelectedTimesheets([]);
    setSelectedCoaches([]);
  };

  const handleSelectTimesheet = (timesheetId) => {
    if (!timesheetId) return;
    setSelectedTimesheets((prev) =>
      prev.includes(timesheetId) ? prev.filter((id) => id !== timesheetId) : [...prev, timesheetId]
    );
  };

  const handleSelectAll = () => {
    const currentPageIds = paginatedTimesheets.map((t) => t.id).filter(Boolean);
    const allSelected = currentPageIds.every((id) => selectedTimesheets.includes(id));
    if (allSelected) {
      setSelectedTimesheets((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      setSelectedTimesheets((prev) => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const handleSelectCoach = (coachId) => {
    if (!coachId) return;
    setSelectedCoaches((prev) =>
      prev.includes(coachId) ? prev.filter((id) => id !== coachId) : [...prev, coachId]
    );
  };

  const handleSelectAllCoaches = () => {
    const currentPageIds = paginatedCoaches.map((c) => c.id).filter(Boolean);
    const allSelected = currentPageIds.every((id) => selectedCoaches.includes(id));
    if (allSelected) {
      setSelectedCoaches((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      setSelectedCoaches((prev) => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedTimesheets.length === 0) { showToast("Please select at least one timesheet", "warning"); return; }
    if (!selectedPeriodId) { showToast("Please select a Pay Period before approving.", "warning"); return; }
    setBulkActionLoading(true);
    try {
      const result = await adminTimesheetApi.bulkApproveTimesheets(selectedTimesheets, "", true, selectedPeriodId);
      if (result.success) {
        showToast(result.message || `Successfully approved ${result.data?.successful || 0} timesheet(s)`, "success");
        setSelectedTimesheets([]);
        silentRefresh();
      } else {
        showToast(`Error: ${result.message || "Failed to approve timesheets"}`, "error");
      }
    } catch (error) {
      showToast(`Failed to approve timesheets: ${error.message}`, "error");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedTimesheets.length === 0) { showToast("Please select at least one timesheet", "warning"); return; }
    if (!rejectionReason?.trim()) { showToast("Please provide a rejection reason", "warning"); return; }
    setBulkActionLoading(true);
    try {
      const result = await adminTimesheetApi.bulkRejectTimesheets(selectedTimesheets, rejectionReason);
      if (result.success) {
        showToast(result.message || `Successfully rejected ${result.data?.successful || 0} timesheet(s)`, "success");
        setSelectedTimesheets([]);
        setRejectionReason("");
        setIsRejectModalOpen(false);
        silentRefresh();
      } else {
        showToast(`Error: ${result.message || "Failed to reject timesheets"}`, "error");
      }
    } catch (error) {
      showToast(`Failed to reject timesheets: ${error.message}`, "error");
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTimesheets.length === 0) { showToast("Please select at least one timesheet", "warning"); return; }
    showConfirm("Delete Timesheets", `Delete ${selectedTimesheets.length} selected timesheet(s)? This cannot be undone.`, async () => {
      setBulkActionLoading(true);
      try {
        const result = await adminTimesheetApi.bulkDeleteTimesheets({ entryIds: selectedTimesheets, deleteScope: "entries" });
        if (result.success) {
          showToast(result.message || `Successfully deleted ${result.data?.successful || 0} timesheet(s)`, "success");
          setSelectedTimesheets([]);
          silentRefresh();
        } else {
          showToast(`Error: ${result.message || "Failed to delete timesheets"}`, "error");
        }
      } catch (error) {
        showToast(`Failed to delete timesheets: ${error.message}`, "error");
      } finally {
        setBulkActionLoading(false);
      }
    });
  };

  const handleDeleteCoach = async (coachId, coachName = "this coach") => {
    if (!coachId) { showToast("Invalid coach ID", "error"); return; }
    showConfirm("Delete Coach", `Are you sure you want to delete ${coachName}? This will delete the coach and all related timesheet entries.`, async () => {
      setBulkActionLoading(true);
      try {
        const result = await adminTimesheetApi.bulkDeleteTimesheets({ coachIds: [coachId], deleteScope: "coaches" });
        if (result.success) {
          showToast(result.message || "Coach and related timesheets deleted successfully", "success");
          setSelectedCoaches((prev) => prev.filter((id) => id !== coachId));
          silentRefresh();
        } else {
          showToast(`Error: ${result.message || "Failed to delete coach"}`, "error");
        }
      } catch (error) {
        showToast(`Failed to delete coach: ${error.message}`, "error");
      } finally {
        setBulkActionLoading(false);
      }
    });
  };

  const handleBulkDeleteCoaches = async () => {
    if (selectedCoaches.length === 0) { showToast("Please select at least one coach", "warning"); return; }
    showConfirm("Delete Coaches", `Are you sure you want to delete ${selectedCoaches.length} coach(es)? This will delete the coaches and all related timesheet entries.`, async () => {
      setBulkActionLoading(true);
      try {
        const result = await adminTimesheetApi.bulkDeleteTimesheets({ coachIds: selectedCoaches, deleteScope: "coaches" });
        if (result.success) {
          showToast(result.message || `Deleted ${result.data?.successfulCoachDeletes || 0} coach(es) and related timesheets`, "success");
          setSelectedCoaches([]);
          silentRefresh();
        } else {
          showToast(`Error: ${result.message || "Failed to delete selected coaches"}`, "error");
        }
      } catch (error) {
        showToast(`Failed to bulk delete coaches: ${error.message}`, "error");
      } finally {
        setBulkActionLoading(false);
      }
    });
  };

  // ─── Pagination ─────────────────────────────────────────────────────────────
  const totalCoachPages = Math.max(1, Math.ceil(filteredCoaches.length / coachesPerPage));
  const filteredTimesheets = timesheets.filter((ts) => {
    if (!timesheetSearchTerm.trim()) return true;
    const q = timesheetSearchTerm.toLowerCase();
    const searchableDate = ts.date ? new Date(ts.date).toLocaleDateString("en-US").toLowerCase() : "";
    return [ts.name, ts.email, ts.location, ts.notes, ts.status, ts.totalHours?.toString(), searchableDate]
      .filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });
  const totalTimesheetPages = Math.max(1, Math.ceil(filteredTimesheets.length / timesheetsPerPage));
  const currentCoachesPage = Math.min(coachesPage, totalCoachPages);
  const currentTimesheetsPage = Math.min(timesheetsPage, totalTimesheetPages);
  const paginatedCoaches = filteredCoaches.slice((currentCoachesPage - 1) * coachesPerPage, currentCoachesPage * coachesPerPage);
  const paginatedTimesheets = filteredTimesheets.slice((currentTimesheetsPage - 1) * timesheetsPerPage, currentTimesheetsPage * timesheetsPerPage);

  // ─── Coach pending counts (memoised per render) ──────────────────────────
  const pendingByCoach = React.useMemo(() => {
    const map = {};
    timesheets.forEach((ts) => {
      if (ts.status === "submitted") map[ts.coachId] = (map[ts.coachId] || 0) + 1;
    });
    return map;
  }, [timesheets]);

  const periodOptions = React.useMemo(() => [
    ...payPeriods,
    { id: '__custom__', label: 'Custom Date Range', startDate: '', endDate: '' }
  ], [payPeriods]);

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-500 mb-1">Dashboard / Admin / Timesheets</p>
        <h1 className="text-3xl font-bold text-gray-900">Timesheet Management</h1>
        <p className="text-gray-600 mt-1">Review and manage coach timesheet submissions</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Timesheets", value: stats.total,    Icon: Calendar,     bg: "bg-blue-100",   icon: "text-blue-600"  },
          { label: "Pending Review",   value: stats.pending,  Icon: Eye,          bg: "bg-yellow-100", icon: "text-yellow-600"},
          { label: "Approved",         value: stats.approved, Icon: CheckCircle,  bg: "bg-green-100",  icon: "text-green-600" },
          { label: "Rejected",         value: stats.rejected, Icon: XCircle,      bg: "bg-red-100",    icon: "text-red-600"   },
        ].map(({ label, value, Icon, bg, icon }) => (
          <div key={label} className="bg-white rounded-xl shadow-sm p-5 flex items-center gap-4">
            <div className={`p-2 ${bg} rounded-lg shrink-0`}><Icon className={`h-6 w-6 ${icon}`} /></div>
            <div>
              <p className="text-sm font-medium text-gray-500">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value || 0}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm px-5 py-4 mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">
          {activeView === "coaches" ? `Coaches (${filteredCoaches.length})` : "All Timesheets"}
        </h2>

        <div className="flex flex-wrap items-center gap-3">
          {/* View-mode switcher — only in coaches view */}
          {activeView === "coaches" && (
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden text-sm">
              {VIEW_MODES.map(({ key, label, Icon, title }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key)}
                  title={title}
                  className={`flex items-center gap-1.5 px-3 py-2 font-medium transition ${
                    viewMode === key ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          )}

          <button
            onClick={handleViewToggle}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
          >
            <Users className="h-4 w-4" />
            {activeView === "coaches" ? "View All Timesheets" : "View Coaches"}
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* COACHES VIEW                                                       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {activeView === "coaches" ? (
        <div className="bg-white rounded-xl shadow-sm p-5">
          {/* Search + bulk delete */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
            <div className="w-full max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Search Coaches</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={paginatedCoaches.length > 0 && paginatedCoaches.every((c) => selectedCoaches.includes(c.id))}
                  onChange={handleSelectAllCoaches}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Select Page
              </label>
              {selectedCoaches.length > 0 && (
                <button
                  onClick={handleBulkDeleteCoaches}
                  disabled={bulkActionLoading}
                  className="flex items-center gap-2 bg-gray-800 hover:bg-black disabled:bg-gray-500 text-white font-semibold px-4 py-2 rounded-lg text-sm transition"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Selected ({selectedCoaches.length})
                </button>
              )}
            </div>
          </div>

          {/* ── TABLE VIEW (default) ──────────────────────────────────────────── */}
          {viewMode === "table" && (
            <div className="overflow-x-auto -mx-1">
              <table className="min-w-full text-sm border-separate border-spacing-0">
                <thead>
                  <tr>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-l border-gray-200 rounded-tl-lg text-left w-8">
                      <input
                        type="checkbox"
                        checked={paginatedCoaches.length > 0 && paginatedCoaches.every((c) => selectedCoaches.includes(c.id))}
                        onChange={handleSelectAllCoaches}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                      />
                    </th>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Coach</th>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-gray-200 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Timesheets</th>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-gray-200 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Approved</th>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-gray-200 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Pending</th>
                    <th className="sticky top-0 px-3 py-2.5 bg-gray-100 border-y border-r border-gray-200 rounded-tr-lg text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoaches.length > 0 ? (
                    paginatedCoaches.map((coach, idx) => {
                      const pending = pendingByCoach[coach.id] || 0;
                      const approved = timesheets.filter(ts => ts.coachId === coach.id && ts.status === "approved").length;
                      const isLast = idx === paginatedCoaches.length - 1;
                      return (
                        <tr
                          key={coach.id}
                          className="group hover:bg-blue-50 transition cursor-pointer"
                          onClick={() => handleCoachClick(coach.id)}
                        >
                          <td className={`px-3 py-2 border-b border-l border-gray-200 ${isLast ? "rounded-bl-lg" : ""}`} onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedCoaches.includes(coach.id)}
                              onChange={() => handleSelectCoach(coach.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            />
                          </td>
                          <td className="px-3 py-2 border-b border-gray-200 text-xs text-gray-400 tabular-nums w-8">
                            {(currentCoachesPage - 1) * coachesPerPage + idx + 1}
                          </td>
                          <td className="px-3 py-2 border-b border-gray-200 whitespace-nowrap">
                            <span className="font-semibold text-gray-900 text-sm group-hover:text-blue-700 transition">{coach.name}</span>
                          </td>
                          <td className="px-3 py-2 border-b border-gray-200 text-gray-500 text-xs whitespace-nowrap">{coach.email}</td>
                          <td className="px-3 py-2 border-b border-gray-200 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
                              {coach.timesheetCount}
                            </span>
                          </td>
                          <td className="px-3 py-2 border-b border-gray-200 text-center">
                            {approved > 0 ? (
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                {approved}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 border-b border-gray-200 text-center">
                            {pending > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block animate-pulse" />
                                {pending}
                              </span>
                            ) : (
                              <span className="text-gray-300 text-xs">—</span>
                            )}
                          </td>
                          <td className={`px-3 py-2 border-b border-r border-gray-200 text-center ${isLast ? "rounded-br-lg" : ""}`} onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteCoach(coach.id, coach.name || "this coach"); }}
                              disabled={bulkActionLoading}
                              className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200 disabled:opacity-50 transition"
                              title="Delete coach"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center border border-gray-200 rounded-b-lg">
                        <EmptyCoaches searchTerm={searchTerm} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* ── LIST VIEW ─────────────────────────────────────────────────────── */}
          {viewMode === "list" && (
            <div className="space-y-3">
              {filteredCoaches.length > 0 ? (
                paginatedCoaches.map((coach) => {
                  const pending = pendingByCoach[coach.id] || 0;
                  return (
                    <div
                      key={coach.id}
                      className="flex items-center gap-4 px-4 py-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-sm transition cursor-pointer bg-white"
                      onClick={() => handleCoachClick(coach.id)}
                    >
                      <label className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedCoaches.includes(coach.id)}
                          onChange={() => handleSelectCoach(coach.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        />
                      </label>
                      <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900">{coach.name}</p>
                        <p className="text-xs text-gray-500 truncate">{coach.email}</p>
                      </div>
                      <div className="flex items-center gap-4 shrink-0 text-sm">
                        <div className="text-center hidden sm:block">
                          <p className="font-semibold text-gray-900">{coach.timesheetCount}</p>
                          <p className="text-xs text-gray-400">sheets</p>
                        </div>
                        {pending > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                            {pending} pending
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">All reviewed</span>
                        )}
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteCoach(coach.id, coach.name || "this coach"); }}
                          disabled={bulkActionLoading}
                          className="inline-flex items-center justify-center p-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                          title="Delete coach"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <EmptyCoaches searchTerm={searchTerm} />
              )}
            </div>
          )}

          {/* ── CARDS VIEW ────────────────────────────────────────────────────── */}
          {viewMode === "cards" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredCoaches.length > 0 ? (
                paginatedCoaches.map((coach) => {
                  const pending = pendingByCoach[coach.id] || 0;
                  return (
                    <div
                      key={coach.id}
                      className="bg-gray-50 rounded-xl p-5 border border-gray-200 hover:border-blue-300 hover:shadow-md transition cursor-pointer flex flex-col gap-3"
                      onClick={() => handleCoachClick(coach.id)}
                    >
                      <div className="flex items-start justify-between">
                        <label className="shrink-0" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedCoaches.includes(coach.id)}
                            onChange={() => handleSelectCoach(coach.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                          />
                        </label>
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteCoach(coach.id, coach.name || "this coach"); }}
                            disabled={bulkActionLoading}
                            className="inline-flex items-center justify-center p-1.5 rounded-md bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                            title="Delete coach"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                          <Eye className="h-4 w-4 text-gray-400" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{coach.name}</p>
                          <p className="text-xs text-gray-500 truncate">{coach.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-1 border-t border-gray-200">
                        <span className="text-xs text-gray-500">{coach.timesheetCount} timesheets</span>
                        {pending > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                            {pending} pending
                          </span>
                        ) : (
                          <span className="text-xs text-green-600 font-medium">All reviewed ✓</span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full">
                  <EmptyCoaches searchTerm={searchTerm} />
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {filteredCoaches.length > 0 && (
            <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-500">
                Showing {filteredCoaches.length} coach{filteredCoaches.length !== 1 ? "es" : ""}
                {filteredCoaches.length < coaches.length && ` (filtered from ${coaches.length})`}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-sm text-gray-500">Per page</label>
                <select
                  value={coachesPerPage}
                  onChange={(e) => setCoachesPerPage(Number(e.target.value))}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value={200}>All</option>
                  <option value={8}>8</option>
                  <option value={16}>16</option>
                  <option value={24}>24</option>
                </select>
                {totalCoachPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCoachesPage((p) => Math.max(1, p - 1))}
                      disabled={currentCoachesPage === 1}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                    >Previous</button>
                    <span className="text-sm text-gray-700">Page {currentCoachesPage} of {totalCoachPages}</span>
                    <button
                      onClick={() => setCoachesPage((p) => Math.min(totalCoachPages, p + 1))}
                      disabled={currentCoachesPage === totalCoachPages}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                    >Next</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* ═══════════════════════════════════════════════════════════════════ */
        /* TIMESHEETS VIEW                                                    */
        /* ═══════════════════════════════════════════════════════════════════ */
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">All Timesheets</h2>
                <p className="text-gray-500 text-sm mt-0.5">Search and review timesheets across all coaches</p>
                <div className="relative max-w-md mt-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by coach, email, location, notes, status…"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    value={timesheetSearchTerm}
                    onChange={(e) => setTimesheetSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              {selectedTimesheets.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <Autocomplete
                    size="small"
                    options={payPeriods}
                    getOptionLabel={getPeriodDisplayLabel}
                    value={payPeriods.find(p => p.id === selectedPeriodId) || null}
                    onChange={(e, newValue) => setSelectedPeriodId(newValue ? newValue.id : '')}
                    renderInput={(params) => <TextField {...params} placeholder="Search Pay Period..." />}
                    sx={{ minWidth: 220, bgcolor: 'white', '& .MuiOutlinedInput-root': { height: '36px' } }}
                    noOptionsText="No pay periods available"
                  />
                  <button onClick={handleBulkApprove} disabled={bulkActionLoading}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold px-4 py-2 rounded-lg text-sm transition">
                    <CheckCircle className="h-4 w-4" /> Approve ({selectedTimesheets.length})
                  </button>
                  <button onClick={() => setIsRejectModalOpen(true)} disabled={bulkActionLoading}
                    className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold px-4 py-2 rounded-lg text-sm transition">
                    <XCircle className="h-4 w-4" /> Reject ({selectedTimesheets.length})
                  </button>
                  <button onClick={handleBulkDelete} disabled={bulkActionLoading}
                    className="flex items-center gap-2 bg-gray-800 hover:bg-black disabled:bg-gray-500 text-white font-semibold px-4 py-2 rounded-lg text-sm transition">
                    <Trash2 className="h-4 w-4" /> Delete ({selectedTimesheets.length})
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-center">
                    <input type="checkbox"
                      checked={paginatedTimesheets.length > 0 && paginatedTimesheets.every((t) => selectedTimesheets.includes(t.id))}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  </th>
                  {["Coach", "Date", "Location", "Hours & Times", "Pay Amount", "Notes", "Status"].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredTimesheets.length > 0 ? (
                  paginatedTimesheets.map((ts) => (
                    <tr key={ts.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-center">
                        <input type="checkbox"
                          checked={selectedTimesheets.includes(ts.id)}
                          onChange={() => handleSelectTimesheet(ts.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{ts.name || "Unknown Coach"}</div>
                        <div className="text-gray-400 text-xs">{ts.email || "No email"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {new Date(ts.date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs truncate text-gray-700" title={ts.location}>{ts.location || "—"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{ts.totalHours || 0} hrs</div>
                        <div className="text-xs text-gray-400">{ts.startTime || "N/A"} – {ts.endTime || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {ts.status === "approved" ? (
                          <div className="font-medium text-green-700">
                            {ts.employmentType === "volunteer" ? "$0.00" : `$${(ts.amountOwed || 0).toFixed(2)}`}
                            {ts.employmentType === "volunteer" && <span className="ml-1 text-xs font-normal text-gray-400">(Vol.)</span>}
                          </div>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs truncate text-gray-600" title={ts.notes}>{ts.notes || "—"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={ts.status} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-10 text-center">
                      <Calendar className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                      <p className="text-gray-400">No timesheets found</p>
                      {timesheetSearchTerm && <p className="text-gray-300 text-sm mt-1">No results for "{timesheetSearchTerm}"</p>}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredTimesheets.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-500">
                Showing {(currentTimesheetsPage - 1) * timesheetsPerPage + 1}–
                {Math.min(currentTimesheetsPage * timesheetsPerPage, filteredTimesheets.length)} of {filteredTimesheets.length}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-sm text-gray-500">Per page</label>
                <select value={timesheetsPerPage} onChange={(e) => setTimesheetsPerPage(Number(e.target.value))}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500">
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <button onClick={() => setTimesheetsPage((p) => Math.max(1, p - 1))} disabled={currentTimesheetsPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Previous</button>
                <span className="text-sm text-gray-700">Page {currentTimesheetsPage} of {totalTimesheetPages}</span>
                <button onClick={() => setTimesheetsPage((p) => Math.min(totalTimesheetPages, p + 1))} disabled={currentTimesheetsPage === totalTimesheetPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Bulk Reject Modal ─────────────────────────────────────────────── */}
      {isRejectModalOpen && (
        <Modal onClose={() => { setIsRejectModalOpen(false); setRejectionReason(""); }}>
          <h2 className="text-xl font-bold mb-3">Reject Timesheets</h2>
          <p className="text-sm text-gray-500 mb-4">
            You are about to reject {selectedTimesheets.length} timesheet(s). Please provide a reason.
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 min-h-[100px] text-sm"
            placeholder="Enter rejection reason…"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => { setIsRejectModalOpen(false); setRejectionReason(""); }} disabled={bulkActionLoading}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm disabled:opacity-50">Cancel</button>
            <button onClick={handleBulkReject} disabled={bulkActionLoading || !rejectionReason.trim()}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm disabled:opacity-50">
              {bulkActionLoading ? "Rejecting…" : "Reject"}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── Coach Review Modal ────────────────────────────────────────────── */}
      {reviewCoach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeReview}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
              <h2 className="text-xl font-bold text-gray-900">Review — {reviewCoach.name}</h2>
              <button onClick={closeReview} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="p-6">
              {reviewLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
                    <p className="mt-3 text-gray-500 text-sm">Loading timesheets…</p>
                  </div>
                </div>
              ) : reviewTimesheets.length === 0 ? (
                <div className="text-center py-16">
                  <Calendar className="h-14 w-14 text-gray-200 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-500">No Timesheets Found</h3>
                  <p className="text-gray-400 text-sm mt-1">This coach hasn't submitted any timesheets yet.</p>
                </div>
              ) : payPeriods.length === 0 ? (
                <div className="text-center py-16">
                  <Calendar className="h-14 w-14 text-gray-200 mx-auto mb-3" />
                  <h3 className="text-base font-medium text-gray-500">No Pay Periods</h3>
                  <p className="text-gray-400 text-sm mt-1">Create a pay period in Payroll Management first.</p>
                </div>
              ) : (() => {
                const isCustomDate = selectedPeriodId === '__custom__';
                const selPeriod = isCustomDate ? null : (selectedPeriodId ? payPeriods.find(p => p.id === selectedPeriodId) : null);
                const periodStart = isCustomDate
                  ? (customStartDate ? dayjs(customStartDate) : null)
                  : (selPeriod?.startDate ? dayjs(selPeriod.startDate) : null);
                const periodEnd = isCustomDate
                  ? (customEndDate ? dayjs(customEndDate) : null)
                  : (selPeriod?.endDate ? dayjs(selPeriod.endDate) : null);
                const hasDateRange = isCustomDate ? (customStartDate && customEndDate) : !!selPeriod;
                const visibleTimesheets = hasDateRange
                  ? reviewTimesheets.filter(ts => {
                      if (!ts.date) return false;
                      const d = dayjs(ts.date);
                      return d.isValid() && (d.isSame(periodStart, 'day') || d.isAfter(periodStart)) && (d.isSame(periodEnd, 'day') || d.isBefore(periodEnd));
                    })
                  : [];
                const pendingEntries = visibleTimesheets.filter((ts) => ts.status === "submitted");
                const selectedEntries = visibleTimesheets.filter((ts) => reviewSelectedIds.includes(ts.id));
                const totalSelectedHours = selectedEntries.reduce((s, ts) => s + (parseFloat(ts.totalHours) || 0), 0);
                const rate = reviewPayrollProfile?.hourlyRate || 0;
                const amount = totalSelectedHours * rate;
                const allSelected = pendingEntries.length > 0 && pendingEntries.every((ts) => reviewSelectedIds.includes(ts.id));
                const hasAnyEntries = visibleTimesheets.length > 0;

                return (
                  <div className="space-y-5">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
                      <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Timesheet Review</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm">
                        <div><span className="font-medium text-gray-600">Coach:</span><span className="ml-2 text-gray-900">{reviewCoach.name}</span></div>
                        <div><span className="font-medium text-gray-600">Job Title:</span><span className="ml-2 text-gray-900">{reviewPayrollProfile?.jobTitle || "Coach"}</span></div>
                        <div><span className="font-medium text-gray-600">Employment:</span><span className="ml-2 text-gray-900">{reviewPayrollProfile?.employmentType === "volunteer" ? "Volunteer" : "Paid Coach"}</span></div>
                         <div><span className="font-medium text-gray-600">Pay Period:</span>
                             <span className="ml-2 inline-block min-w-[240px]">
                                <Autocomplete
                                  size="small"
                                  options={periodOptions}
                                  getOptionLabel={(p) => p.label || getPeriodDisplayLabel(p)}
                                  value={periodOptions.find(p => p.id === (selectedPeriodId || '')) || null}
                                  onChange={(e, newValue) => {
                                    setSelectedPeriodId(newValue ? newValue.id : '');
                                    if (newValue?.id !== '__custom__') {
                                      setCustomStartDate('');
                                      setCustomEndDate('');
                                    }
                                  }}
                                  ListboxProps={{ style: { maxHeight: 170, paddingBottom: 0 } }}
                                  renderOption={(props, option) => {
                                   const isCustom = option.id === '__custom__';
                                   return (
                                     <li {...props} style={{
                                       ...props.style,
                                       position: isCustom ? 'sticky' : undefined,
                                       bottom: isCustom ? 0 : undefined,
                                       backgroundColor: isCustom ? '#eff6ff' : undefined,
                                       borderTop: isCustom ? '2px solid #bfdbfe' : undefined,
                                       fontWeight: isCustom ? 600 : undefined,
                                       color: isCustom ? '#1d4ed8' : undefined,
                                       zIndex: isCustom ? 10 : undefined,
                                     }}>
                                       {isCustom && <span className="mr-1.5 text-blue-500">+</span>}
                                       {option.label || getPeriodDisplayLabel(option)}
                                     </li>
                                   );
                                 }}
                                 renderInput={(params) => <TextField {...params} placeholder="Search Pay Period..." />}
                                 sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { height: '34px', fontSize: '0.875rem' } }}
                                 noOptionsText="No pay periods available"
                                 disableClearable={!selectedPeriodId}
                               />
                             </span>
                         </div>
                        {!isCustomDate && selectedPeriodId && selPeriod && (
                          <>
                            <div>
                              <span className="font-medium text-gray-600">Start Date:</span>
                              <span className="ml-2 text-gray-900">
                                {(() => {
                                  const d = selPeriod.startDate;
                                  if (!d) return '—';
                                  const dt = typeof d === 'object' && '_seconds' in d ? dayjs.unix(d._seconds) : dayjs(d);
                                  return dt.isValid() ? dt.format('MM-DD-YY') : '—';
                                })()}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-600">End Date:</span>
                              <span className="ml-2 text-gray-900">
                                {(() => {
                                  const d = selPeriod.endDate;
                                  if (!d) return '—';
                                  const dt = typeof d === 'object' && '_seconds' in d ? dayjs.unix(d._seconds) : dayjs(d);
                                  return dt.isValid() ? dt.format('MM-DD-YY') : '—';
                                })()}
                              </span>
                            </div>
                          </>
                        )}
                        {isCustomDate && (
                          <>
                            <div>
                              <span className="font-medium text-gray-600">Start Date:</span>
                              <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                            <div>
                              <span className="font-medium text-gray-600">End Date:</span>
                              <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                          </>
                        )}
                        {selPeriod?.payOutDate && (
                          <div>
                            <span className="font-medium text-gray-600">Pay Out Date:</span>
                            <span className="ml-2 text-gray-900">
                              {(() => {
                                const d = selPeriod.payOutDate;
                                const dt = typeof d === 'object' && '_seconds' in d ? dayjs.unix(d._seconds) : dayjs(d);
                                return dt.isValid() ? dt.format('MM-DD-YY') : '—';
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">Time Entries</h4>
                        {pendingEntries.length > 0 && (
                          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() => {
                                if (allSelected) {
                                  setReviewSelectedIds([]);
                                } else {
                                  setReviewSelectedIds(pendingEntries.map((ts) => ts.id));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                            />
                            {allSelected ? "Deselect All" : "Select All"}
                          </label>
                        )}
                      </div>
                      {!hasAnyEntries ? (
                        <div className="text-center py-12">
                          <Calendar className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm font-medium">
                            {isCustomDate ? 'No Timesheets Found' : (selectedPeriodId ? 'No Timesheets Found' : 'Select a Pay Period')}
                          </p>
                          <p className="text-gray-400 text-xs mt-0.5">
                            {isCustomDate
                              ? "No timesheets match the selected custom date range."
                              : (selectedPeriodId
                                ? "This coach hasn't submitted any timesheets for this period."
                                : 'Choose a pay period above to view timesheets.')}
                          </p>
                        </div>
                      ) : (
                      <div className="space-y-1.5">
                        {visibleTimesheets.map((ts) => {
                          const isPending = ts.status === "submitted";
                          const isRejected = ts.status === "rejected";
                          const isApproved = ts.status === "approved";
                          const isSelected = reviewSelectedIds.includes(ts.id);
                          return (
                            <div
                              key={ts.id}
                              className={`rounded-lg px-4 py-2.5 border text-sm flex items-center justify-between transition ${
                                isRejected ? 'bg-red-50 border-red-200'
                                : isApproved ? 'bg-green-50 border-green-200'
                                : isSelected ? 'bg-blue-50 border-blue-200'
                                : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                {isPending ? (
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      setReviewSelectedIds((prev) =>
                                        prev.includes(ts.id) ? prev.filter((id) => id !== ts.id) : [...prev, ts.id]
                                      );
                                    }}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 shrink-0"
                                  />
                                ) : (
                                  <div className="w-4 h-4 shrink-0" />
                                )}
                                <span className="font-medium text-gray-900 w-16 shrink-0">{ts.date ? dayjs.utc(ts.date).format("MMM D") : ""}</span>
                                <span className="text-gray-600 w-32 truncate">{ts.location || "N/A"}</span>
                                <span className="text-gray-500 text-xs">{ts.startTime || "N/A"} – {ts.endTime || "N/A"}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0 ml-2">
                                <span className="font-semibold text-gray-900">{ts.totalHours || 0} hr{ts.totalHours !== 1 ? "s" : ""}</span>
                                <StatusBadge status={ts.status} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      )}
                    </div>

                    {pendingEntries.length > 0 && (
                      <>
                        <hr className="border-gray-200" />

                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Selected Hours</span>
                            <span className="font-semibold">{totalSelectedHours.toFixed(1)} hr{totalSelectedHours !== 1 ? "s" : ""}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Hourly Rate</span>
                            <span className="font-semibold">{rate > 0 ? `$${rate.toFixed(2)} / hr` : reviewPayrollProfile?.employmentType === "volunteer" ? "Volunteer" : "N/A — set rate in Payroll"}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-2 text-base">
                            <span className="font-medium text-gray-800">Amount Owed</span>
                            <span className="font-bold text-gray-900 text-lg">{rate > 0 && reviewSelectedIds.length > 0 ? `$${amount.toFixed(2)}` : "N/A"}</span>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Admin Notes</label>
                          <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[70px] text-sm"
                            placeholder="Add notes about this timesheet…"
                            value={reviewAdminNotes}
                            onChange={(e) => setReviewAdminNotes(e.target.value)}
                          />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                          {!reviewPayrollProfile?.hourlyRate && reviewPayrollProfile?.employmentType !== "volunteer" ? (
                            <div className="flex-1 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 text-center">
                              Set an hourly rate in the coach's Payroll Profile before approving.
                            </div>
                          ) : (
                            <button onClick={handleReviewApproveAll} disabled={reviewActionLoading || reviewSelectedIds.length === 0}
                              className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-semibold px-4 py-3 rounded-lg text-sm transition">
                              {reviewActionLoading ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                              {rate > 0 && reviewSelectedIds.length > 0 ? `APPROVE $${amount.toFixed(2)}` : "APPROVE"}
                            </button>
                          )}
                          <button onClick={() => { setReviewActionType("reject"); setReviewRejectOpen(true); setReviewRejectReason(""); }} disabled={reviewActionLoading || reviewSelectedIds.length === 0}
                            className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold px-4 py-3 rounded-lg text-sm transition">
                            <XCircle className="h-4 w-4" /> Reject Selected
                          </button>
                          <button onClick={() => { setReviewActionType("correction"); setReviewRejectOpen(true); setReviewRejectReason(""); }} disabled={reviewActionLoading || reviewSelectedIds.length === 0}
                            className="flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-semibold px-4 py-3 rounded-lg text-sm transition">
                            <Send className="h-4 w-4" /> Request Correction
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* ─── Review Reject / Correction Modal ─────────────────────────────── */}
      {reviewRejectOpen && (
        <Modal onClose={() => { setReviewRejectOpen(false); setReviewRejectReason(""); }} zIndex="z-[60]">
          <h2 className="text-xl font-bold mb-3">
            {reviewActionType === "correction" ? "Request Correction" : "Reject Timesheets"}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {reviewActionType === "correction"
              ? `Request correction for ${reviewSelectedIds.length} timesheet(s). Provide details below.`
              : `Reject ${reviewSelectedIds.length} timesheet(s). Provide a reason below.`}
          </p>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {reviewActionType === "correction" ? "Correction Details" : "Rejection Reason"} <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 min-h-[100px] text-sm"
            placeholder={reviewActionType === "correction" ? "Enter details for correction…" : "Enter rejection reason…"}
            value={reviewRejectReason}
            onChange={(e) => setReviewRejectReason(e.target.value)}
          />
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-4">
            <button onClick={() => { setReviewRejectOpen(false); setReviewRejectReason(""); }} disabled={reviewActionLoading}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm disabled:opacity-50">Cancel</button>
            <button
              onClick={reviewActionType === "correction" ? handleConfirmRequestCorrection : handleReviewRejectAll}
              disabled={reviewActionLoading || !reviewRejectReason.trim()}
              className={`px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50 ${reviewActionType === "correction" ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"}`}
            >
              {reviewActionLoading
                ? (reviewActionType === "correction" ? "Requesting…" : "Rejecting…")
                : (reviewActionType === "correction" ? "Request Correction" : "Reject")}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── Confirm Modal ─────────────────────────────────────────────────── */}
      {confirmModal.show && (
        <Modal onClose={() => setConfirmModal({ ...confirmModal, show: false })} zIndex="z-[100]">
          <h2 className="text-xl font-bold mb-2">{confirmModal.title}</h2>
          <p className="text-sm text-gray-500 mb-6">{confirmModal.message}</p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setConfirmModal({ ...confirmModal, show: false })}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm">Cancel</button>
            <button
              onClick={() => { const cb = confirmModal.onConfirm; setConfirmModal({ ...confirmModal, show: false }); if (cb) cb(); }}
              className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm">Confirm</button>
          </div>
        </Modal>
      )}

      {/* ─── Create Pay Period Modal ──────────────────────────────────────── */}
      {showCreatePeriodModal && (
        <Modal onClose={() => setShowCreatePeriodModal(false)} zIndex="z-[70]">
          <h2 className="text-xl font-bold mb-4">Create Pay Period</h2>
          <p className="text-sm text-gray-500 mb-4">
            No pay period exists for <strong>{customStartDate} – {customEndDate}</strong>.
            Create one to continue with approval.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={periodForm.startDate}
                onChange={(e) => setPeriodForm({ ...periodForm, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={periodForm.endDate}
                onChange={(e) => setPeriodForm({ ...periodForm, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label <span className="text-gray-400">(optional)</span></label>
              <input
                type="text"
                value={periodForm.label}
                onChange={(e) => setPeriodForm({ ...periodForm, label: e.target.value })}
                placeholder="e.g. June-July 2026"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowCreatePeriodModal(false)}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!periodForm.startDate || !periodForm.endDate) {
                  showToast("Please fill in both start and end dates.", "warning");
                  return;
                }
                setCreatingPeriod(true);
                try {
                  const label = periodForm.label || `${dayjs(periodForm.startDate).format('MM-DD-YYYY')} – ${dayjs(periodForm.endDate).format('MM-DD-YYYY')}`;
                  const created = await payrollApi.createPayPeriod({ startDate: periodForm.startDate, endDate: periodForm.endDate, label });
                  const newId = created?.id;
                  showToast("Pay period created. You can now approve the timesheets.", "success");
                  setShowCreatePeriodModal(false);
                  await loadPayPeriods();
                  if (newId) setSelectedPeriodId(newId);
                } catch (err) {
                  showToast("Failed to create pay period: " + err.message, "error");
                } finally {
                  setCreatingPeriod(false);
                }
              }}
              disabled={creatingPeriod || !periodForm.startDate || !periodForm.endDate}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm disabled:opacity-50"
            >
              {creatingPeriod ? 'Creating…' : 'Create & Continue'}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── Toast ─────────────────────────────────────────────────────────── */}
      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        .animate-slide-in { animation: slideIn 0.3s ease-out; }
      `}</style>
      {toast.show && (
        <div
          onClick={() => setToast((p) => ({ ...p, show: false }))}
          className={`fixed top-4 right-4 z-[100] flex items-center gap-3 px-5 py-4 rounded-lg shadow-lg text-sm font-medium cursor-pointer animate-slide-in ${
            toast.type === "error" ? "bg-red-50 text-red-800 border border-red-200"
            : toast.type === "warning" ? "bg-amber-50 text-amber-800 border border-amber-200"
            : "bg-green-50 text-green-800 border border-green-200"
          }`}
        >
          {toast.type === "error" ? <XCircle size={18} className="text-red-500 shrink-0" />
            : toast.type === "warning" ? <Clock size={18} className="text-amber-500 shrink-0" />
            : <CheckCircle size={18} className="text-green-500 shrink-0" />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

const EmptyCoaches = ({ searchTerm }) => (
  <div className="text-center py-14">
    <Users className="h-10 w-10 text-gray-200 mx-auto mb-2" />
    <p className="text-gray-400">No coaches found</p>
    {searchTerm && <p className="text-gray-300 text-sm mt-1">No matches for "{searchTerm}"</p>}
  </div>
);

const Modal = ({ children, onClose, zIndex = "z-50" }) => (
  <div className={`fixed inset-0 ${zIndex} flex items-center justify-center bg-black/50`} onClick={onClose}>
    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

export default AdminTimesheetDashboard;